// Global Instructions Rule Applied!
//
// Phase 5 — Drafts tab.
//
// The original Phase 2 EmailCampaignsPage content, lifted into a tab.
// Filters view__email_campaigns to status='draft' so the audit-log Sent
// log tab can own everything past 'queued'. Uses useEmailCampaigns with
// the status filter pre-applied; behaviour otherwise unchanged from the
// Phase 2 grid the compose form returns to.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { EmailCampaignGridModel } from '../../../state_manager/data_models/email_campaign_grid_model.ts'
import { useEmailCampaigns } from '../hooks/useEmailCampaigns.ts'
import { deleteEmailCampaign } from '../services/emailService.ts'

const EMAIL_MODULE = 'email'

const DRAFTS_INITIAL_FILTERS = [
  { key: 'email_campaigns__status', value: ['draft'] }
]

const formatDate = (iso?: string | null) => {
  if (!iso) return <span style={{ color: '#9ca3af' }}>—</span>
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export default function DraftsTab() {
  const {
    campaignsData,
    campaignsLoading,
    activeSorting,
    handleSetFilters,
    handleSetSorting,
    refetch
  } = useEmailCampaigns({ initialFilters: DRAFTS_INITIAL_FILTERS })
  const { openFormInTab, openModuleTab } = useTabManagerContext()
  const location = useLocation()

  const openSettings = useCallback(() => {
    openModuleTab('email-settings')
  }, [openModuleTab])

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const currentSort = activeSorting?.[0]

  // Refetch when returning to the grid root after create/edit/cancel.
  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (location.pathname === `/${EMAIL_MODULE}` && prev !== location.pathname) {
      refetch()
    }
  }, [location.pathname, refetch])

  const openCreate = useCallback(() => {
    openFormInTab(EMAIL_MODULE, null)
  }, [openFormInTab])

  const openEdit = useCallback(
    (record: EmailCampaignGridModel) => {
      openFormInTab(EMAIL_MODULE, record.email_campaigns__id, record.email_campaigns__name)
    },
    [openFormInTab]
  )

  const handleDelete = useCallback(
    async (record: EmailCampaignGridModel) => {
      setDeletingId(record.email_campaigns__id)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await deleteEmailCampaign(record.email_campaigns__id, token)
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete email campaign')
          return
        }
        toast.success('Email campaign deleted')
        await refetch()
      } finally {
        setDeletingId(null)
      }
    },
    [refetch]
  )

  const columns: ColumnsType<EmailCampaignGridModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'email_campaigns__name',
        key: 'email_campaigns__name',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__name'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (name: string, record: EmailCampaignGridModel) => (
          <a onClick={() => openEdit(record)} className='font-medium'>
            {name || '(untitled)'}
          </a>
        )
      },
      {
        title: 'Subject',
        dataIndex: 'email_campaigns__subject',
        key: 'email_campaigns__subject',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__subject'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (subject: string) =>
          subject ? subject : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Template',
        dataIndex: 'email_campaigns__email_template_name',
        key: 'email_campaigns__email_template_name',
        width: 180,
        render: (label: string) =>
          label ? <Tag>{label}</Tag> : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Send as',
        dataIndex: 'email_campaigns__sent_as_user_name',
        key: 'email_campaigns__sent_as_user_name',
        width: 180,
        render: (name: string) =>
          name ? name : <span style={{ color: '#9ca3af' }}>—</span>
      },
      {
        title: 'Recipients',
        dataIndex: 'email_campaigns__recipient_count',
        key: 'email_campaigns__recipient_count',
        width: 110,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'email_campaigns__recipient_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (n: number) => (n > 0 ? n : <span style={{ color: '#9ca3af' }}>—</span>)
      },
      {
        title: 'Last modified',
        dataIndex: 'email_campaigns__date_modified',
        key: 'email_campaigns__date_modified',
        width: 180,
        sorter: true,
        defaultSortOrder: 'descend',
        sortOrder:
          currentSort?.key === 'email_campaigns__date_modified'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (iso: string) => formatDate(iso)
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        align: 'center',
        render: (_: unknown, record: EmailCampaignGridModel) => (
          <Space size='small'>
            <Tooltip title='Edit'>
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title='Delete this email draft?'
              description='Attached files stay in storage but the draft and its recipients are soft-deleted.'
              okText='Delete'
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title='Delete'>
                <Button
                  type='text'
                  size='small'
                  danger
                  loading={deletingId === record.email_campaigns__id}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ],
    [currentSort, openEdit, handleDelete, deletingId]
  )

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: any) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter
      if (s && s.order && s.columnKey) {
        handleSetSorting([
          { key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }
        ])
      } else {
        handleSetSorting([{ key: 'email_campaigns__date_modified', direction: 'desc' }])
      }
    },
    [handleSetSorting]
  )

  const onSearch = useCallback(
    (value: string) => {
      const v = (value || '').trim()
      // Always re-pin the status='draft' filter on this tab.
      handleSetFilters(
        v
          ? [{ key: 'email_campaigns__status', value: ['draft'] }, { key: 'email_campaigns__name', value: v }]
          : [{ key: 'email_campaigns__status', value: ['draft'] }]
      )
    },
    [handleSetFilters]
  )

  return (
    <div className='flex flex-col h-full'>
      <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
        <Space>
          <Input.Search
            placeholder='Search drafts by name...'
            allowClear
            onSearch={onSearch}
            style={{ width: 260 }}
          />
        </Space>
        <Space>
          <Tooltip title='Email settings (signature, delegations)'>
            <Button icon={<SettingOutlined />} onClick={openSettings} />
          </Tooltip>
          <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
            New Email
          </Button>
        </Space>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table<EmailCampaignGridModel>
          className='email-campaigns-table'
          rowKey='email_campaigns__id'
          columns={columns}
          dataSource={campaignsData}
          loading={campaignsLoading}
          onChange={handleTableChange}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          size='middle'
        />
      </div>
    </div>
  )
}
